import * as pathUtils from "../../utils/path/library";
import {
    emptyAllMeta,
    determineBaseNavSectionOverrides,
    setApp,
    generateApp,
    generateMeta,
} from "./utils";
import { set_app_state } from "../../reducers/App/reducer";
import { set_user_agent } from "../../reducers/Fixed/reducer";
import { getVfUrl } from "../../utils/viafoura/library";
import { constructAdHierarchy } from "../../utils/advertising/library";
import { AD_IU } from "./constants";
import { DEFAULT_SECTION } from "../../constants/content";
import * as CONSTANTS from "./constants";
import { PAGE_TYPES } from "../../constants/tracking";
import {
    HTTP_INTERNAL_SERVER_ERROR,
    httpErrorTitle,
    isValidHttpErrorCode,
} from "../../constants/http";

/**
 * Sets App data and dispatches to store
 *
 * @param {string} path - page path
 * @param {Boolean} sponsored - true if page is sponsored content
 * @param {string} contentId - available if page is a detail pages
 * @param {string} nav - the nav associated with content vertical (e.g. news, sports, arts, etc.). Use when need to present a nav outside of its environment.
 * @param {string} adOrder - used to construct adHierarchy
 * @return {Function} dispatch set_app_state action
 */
export const setAppState =
    (path, sponsored = false, contentId = "", nav = "", adOrder = "") =>
    (dispatch, getState) => {
        const pathForMeta =
            !path || path === "/"
                ? DEFAULT_SECTION
                : pathUtils.getPath(path, true);
        path = pathForMeta.toLowerCase();
        const state = getState();
        const base = pathUtils.getBaseSection(path);
        const baseNavSection = determineBaseNavSectionOverrides(
            base,
            nav,
            path,
            state.featureflags
        );
        const subSection = pathUtils.getSectionByIndex(path, 1);
        const section = pathUtils.getSection(path, Boolean(contentId));
        const { tracking } = state.tracking || {};
        const { statusCode } = state.app || {};

        const pathValue = pathUtils.stripBetaPrefix(
            // Both OG:URL and Canonical URL have to be Absolute path. More doc at Utils library.
            pathUtils.makeAbsoluteCBCDomain(
                // Path exception is to avoid "cbc.ca/homepage"
                pathUtils.pathException(pathForMeta)
            )
        );
        const metaObj = {
            "og:url": pathValue,
            canonical: pathValue,
        };

        const vfUrl = getVfUrl(pathValue);

        if (vfUrl) {
            metaObj["vf:url"] = vfUrl;
        }

        const meta = generateMeta(metaObj);

        const app = setApp(
            generateApp({
                adOrder: adOrder,
                path: path,
                baseSection: base,
                baseNavSection: baseNavSection,
                subSection: subSection,
                section: section,
                sponsored: sponsored,
                contentId: contentId,
                meta: meta,
                statusCode: statusCode,
                defaultHeadlineImage:
                    (state.rightRail && state.rightRail.defaultHeadlineImage) ||
                    (state.page && state.page.defaultHeadlineImage) ||
                    "",
            }),
            tracking
        );

        return dispatch(set_app_state({ app }));
    };
/**
 * Updates path data in App state
 *
 * @param {string} path - page path
 * @param {Boolean} sponsored - true if page is sponsored content
 * @param {string} contentId - available if page is a detail pages
 * @param {string} nav - the nav associated with content vertical (e.g. news, sports, arts, etc.). Use when need to present a nav outside of its environment.
 * @return {Function} dispatch set_app_state action
 */
export const setPathingAppState =
    (path, sponsored = false, contentId = "", nav = "") =>
    (dispatch, getState) => {
        path =
            !path || path === "/" ? DEFAULT_SECTION : pathUtils.getPath(path);
        const state = getState();
        const base = pathUtils.getBaseSection(path);
        const baseNavSection = determineBaseNavSectionOverrides(
            base,
            nav,
            path,
            state.featureflags
        );
        const subSection = pathUtils.getSectionByIndex(path, 1);
        let section = pathUtils.getSection(path, Boolean(contentId));

        const app = setApp(
            generateApp({
                path: path,
                baseSection: base,
                baseNavSection: baseNavSection,
                subSection: subSection,
                section: section,
                sponsored: sponsored,
                contentId: contentId,
                meta: state.app.meta,
                title: state.app.title,
                contentType: state.app.contentType,
            }),
            state.tracking
        );

        return dispatch(set_app_state({ app }));
    };

/**
 * Sets HTTP error status in App state
 *
 * @param {string} path - page path
 * @param {number} code - HTTP error code
 * @return {Function} dispatch set_app_state action
 */
export const setAppError =
    (path = "", code = HTTP_INTERNAL_SERVER_ERROR) =>
    (dispatch) => {
        if (!isValidHttpErrorCode(code)) {
            code = HTTP_INTERNAL_SERVER_ERROR;
        }
        path =
            !path || path === "/" ? DEFAULT_SECTION : pathUtils.getPath(path);
        const base = pathUtils.getBaseSection(path);
        const subSection = pathUtils.getSectionByIndex(path, 1);
        const section = pathUtils.getSection(path, false);
        const ad_hierarchy = constructAdHierarchy(AD_IU, section, "index");
        const title = httpErrorTitle(code);

        const app = generateApp({
            path: path,
            baseSection: base,
            baseNavSection: base,
            subSection: subSection,
            section: section,
            sponsored: false,
            meta: emptyAllMeta(),
            pageType: PAGE_TYPES.BLANK,
            ad_hierarchy: ad_hierarchy,
            title: title,
            statusCode: code,
        });

        return dispatch(set_app_state({ app }));
    };

/**
 * Updates detail page data in App state
 *
 * @param {string} title - contentItem title
 * @param {Object} meta - contentItem metadata
 * @param {string} contentType - contentItem type (e.g. story, photogallery, sponsoredcontent etc)
 * @return {Function} dispatch set_app_state action
 */
export const detailOrAuthorUpdate = (title, meta, contentType) => {
    return (dispatch, getState) => {
        let app = {
            ...getState().app,
        };

        if (title) {
            app.title = title;
        }

        if (meta) {
            // Instruct search engines not to index these content types
            if (CONSTANTS.NO_INDEX_TYPES.includes(contentType)) {
                meta = { ...meta, robots: "noindex" };
            }

            app.meta = meta;
        }

        // NOTE: The way this is setup, it will only work server side
        if (contentType) {
            app.contentType = contentType;
            app.themeClassNames = pathUtils.getPageClassNames(app);
        }

        dispatch(set_app_state({ app }));
    };
};

/**
 * Updates metadata for page in App state
 *
 * @param {string} title - page title
 * @param {Object} meta - page metadata
 * @param {string} pageType - landing, detail, blank etc.
 * @return {Function} dispatch set_app_state action
 */
export const metaUpdate = (title, meta, pageType) => {
    const type = Object.values(PAGE_TYPES).includes(pageType) ? pageType : "";

    return (dispatch, getState) => {
        let app = {
            ...getState().app,
        };
        app.pageType = type;

        if (title) {
            app.title = `${title} | CBC.ca`;
        }

        if (meta) {
            app.meta = meta;
        }

        dispatch(set_app_state({ app }));
    };
};

/**
 * Updates title for page in App state
 *
 * @param {string} title - page title
 * @return {Function} dispatch set_app_state action
 */
export const titleUpdate = (title) => {
    return (dispatch, getState) => {
        let app = {
            ...getState().app,
        };
        app.title = title;

        dispatch(set_app_state({ app }));
    };
};

/**
 * Sets isWebView in Fixed slice of Redux store
 * isWebView indicates whether the session is in an in-app browser
 *
 * @param {string} queryString - Boolean as a string
 * @return {Function} dispatch set_user_agent action
 */
export const checkWebView = (queryString) => {
    return (dispatch) => {
        const isWebView = queryString === "true";
        dispatch(set_user_agent({ isWebView }));
    };
};
